import React, { useState } from 'react';
import './CarePlusCancelModal.css';
import AnimatedPopup from '../../../../../components/AnimatedPopup/AnimatedPopup';
import * as translator from '../../../../../utils/translator';
import * as http from '../../../../../utils/http';
import { PLAN_TYPE_LABEL } from '../../CarePlusOrderDetail/carePlusConstants';
import CarePlusWordmark from '../../CarePlusOrderDetail/CarePlusWordmark';
import cancelIcon from '../../../../../icons/careplus-action-cancel.svg';
import altPauseIcon from '../../../../../icons/careplus-alt-pause.png';
import altDowngradeIcon from '../../../../../icons/careplus-alt-downgrade.png';
import altMedicationIcon from '../../../../../icons/careplus-alt-medication.png';
import altCancelIcon from '../../../../../icons/careplus-alt-cancel.png';
import keepIcon from '../../../../../icons/Mail.svg';
import successBadge from '../../../../../icons/careplus-success-badge-v2.png';
import successCancelOverlay from '../../../../../icons/careplus-success-cancel-overlay-v2.png';

interface CarePlusCancelModalProps {
  subscriptionID: number;
  subscriptionHashID: string;
  planType: 'basic' | 'pro' | 'max' | 'monthly';
  isMonthlyPlan?: boolean;
  language: string;
  country: string;
  isPaused?: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onOpenPause: () => void;
  setStickyNotificationDetails: (details: any) => void;
}

type RefundEstimate = {
  periodMonths: number;
  monthsConsumed: number;
  monthsRemaining: number;
  remainingAmount: number;
  discount: number;
  estimatedRefund: number;
  components?: { treatment: number; service: number; delivery: number; videoCalls: number };
  videoCalls: { allowed: number; used: number; unused: number; ratePerCall: number; amount: number };
};

type CancelScreen = 'alternatives' | 'reasons' | 'refund' | 'confirm';

const CANCEL_REASONS = [
  { id: 'expensive', key: 'CarePlus/cancelModal/reasons/expensive' },
  { id: 'noResults', key: 'CarePlus/cancelModal/reasons/noResults' },
  { id: 'sideEffects', key: 'CarePlus/cancelModal/reasons/sideEffects' },
  { id: 'notSatisfied', key: 'CarePlus/cancelModal/reasons/notSatisfied' },
  { id: 'reachedGoal', key: 'CarePlus/cancelModal/reasons/reachedGoal' },
  { id: 'other', key: 'CarePlus/cancelModal/reasons/other' },
];

const splitFirstWord = (text: string): [string, string] => {
  const [first, ...rest] = (text || '').split(' ');
  return [first || '', rest.join(' ')];
};

const splitLastWord = (text: string): [string, string] => {
  const words = (text || '').split(' ');
  const last = words.pop() || '';
  return [words.join(' '), last];
};

const CarePlusCancelModal: React.FC<CarePlusCancelModalProps> = ({
  subscriptionID,
  subscriptionHashID,
  planType,
  isMonthlyPlan,
  language,
  country,
  isPaused,
  onClose,
  onSuccess,
  onOpenPause,
  setStickyNotificationDetails,
}) => {
  const [screen, setScreen] = useState<CancelScreen>('alternatives');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherComment, setOtherComment] = useState('');
  const [refund, setRefund] = useState<RefundEstimate | null>(null);
  const [loading, setLoading] = useState(false);

  const t = (path: string) => translator.translate(language, 'my_account', path);
  const planTypeLabel = PLAN_TYPE_LABEL[planType];
  const locale = translator.getLocaleByCountry(country);

  const handleAlternativeAction = (action: string) => {
    if (action === 'cancel') {
      setScreen('refund');
      http.getFromServer(`${country}/account/care-plus-subscription/${subscriptionHashID}/refund-estimate`, [http.MIDDLEWARES.CUSTOMER_ID])
        .then((r: any) => setRefund(r.data))
        .catch(() => setRefund(null));
      return;
    }
    if (action === 'keep') { onClose(); return; }
    if (action === 'pause') { if (isPaused) return; onClose(); onOpenPause(); return; }
    if (action === 'downgrade') {
      setLoading(true);
      http.postToServer('account-update/subscription/care-plus/entitlement-change', { hashID: subscriptionHashID, newTier: 'basic' }, [
        http.MIDDLEWARES.CUSTOMER_ID,
        http.MIDDLEWARES.CART_ORDER,
      ])
        .then(() => {
          setStickyNotificationDetails({
            show: true,
            level: 'success',
            title: t('CarePlus/cancelModal/downgradeSuccessTitle'),
            content: t('CarePlus/cancelModal/downgradeSuccessBody'),
          });
          onSuccess();
        })
        .catch(() => setStickyNotificationDetails({
          show: true,
          level: 'error',
          title: t('CarePlus/cancelModal/errorTitle'),
          content: t('CarePlus/cancelModal/errorBody'),
        }))
        .finally(() => setLoading(false));
      return;
    }
    if (action === 'medOnly') {
      setLoading(true);
      http.postToServer('account-update/subscription/care-plus/entitlement-change', { hashID: subscriptionHashID, newTier: 'basic' }, [
        http.MIDDLEWARES.CUSTOMER_ID,
        http.MIDDLEWARES.CART_ORDER,
      ])
        .then(() => {
          setStickyNotificationDetails({
            show: true,
            level: 'success',
            title: t('CarePlus/cancelModal/medOnlySuccessTitle'),
            content: t('CarePlus/cancelModal/medOnlySuccessBody'),
          });
          onSuccess();
        })
        .catch(() => setStickyNotificationDetails({
          show: true,
          level: 'error',
          title: t('CarePlus/cancelModal/errorTitle'),
          content: t('CarePlus/cancelModal/errorBody'),
        }))
        .finally(() => setLoading(false));
    }
  };

  const handleSubmitCancel = () => {
    if (!selectedReason) return;
    setLoading(true);
    http.postToServer('account-update/subscription/care-plus/cancel', {
      subscriptionID,
      reason: selectedReason,
      comment: selectedReason === 'other' ? otherComment : '',
    }, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
      .then(() => {
        setScreen('confirm');
        setStickyNotificationDetails({
          show: true,
          level: 'success',
          title: t('CarePlus/cancelModal/confirmTitle'),
          content: isMonthlyPlan ? t('CarePlus/monthlyPlan/cancelConfirmBody') : (t('CarePlus/cancelModal/confirmBody') || '').replace(':tier', planTypeLabel),
        });
      })
      .catch(() => setStickyNotificationDetails({
        show: true,
        level: 'error',
        title: t('CarePlus/cancelModal/errorTitle'),
        content: t('CarePlus/cancelModal/errorBody'),
      }))
      .finally(() => setLoading(false));
  };

  const [sorryAccent, sorryRest] = splitFirstWord(t('CarePlus/cancelModal/title'));
  const [cancelAccent, cancelRest] = splitFirstWord(t('CarePlus/cancelModal/cancelTitle'));
  const [confirmTitleRest, confirmTitleAccent] = splitLastWord(t('CarePlus/cancelModal/confirmTitle'));
  const [medOnlyTitle, medOnlySuffix] = (t('CarePlus/cancelModal/medOnlyTitle') || '').split(' (');
  const [cancelDescPrefix, cancelDescSuffix] = (t('CarePlus/cancelModal/cancelDesc') || '').split('Care+');
  const [confirmBodyPrefix, confirmBodySuffix] = (t('CarePlus/cancelModal/confirmBody') || '').split(':tier');
  const [confirmRefundPrefix, confirmRefundRest] = (t('CarePlus/cancelModal/confirmRefund') || '').split(':amount');
  const refundAmountText = translator.getPrice(refund?.estimatedRefund ?? 0, locale);

  const renderKeepCancelButtons = (onCancel: () => void, cancelDisabled: boolean) => (
    <div className="careplus-cancel-modal__actions">
      <button
        type="button"
        className="careplus-cancel-modal__btn careplus-cancel-modal__btn--keep"
        onClick={onClose}
      >
        <span className="careplus-cancel-modal__btn-keep-text">{t('CarePlus/cancelModal/keepTitle')}</span>
      </button>
      <button
        type="button"
        className="careplus-cancel-modal__link"
        disabled={cancelDisabled}
        onClick={onCancel}
      >
        <img src={cancelIcon} alt="" className="careplus-cancel-modal__link-icon" />
        {t('CarePlus/pauseModal/cancel')}
      </button>
    </div>
  );

  return (
    <AnimatedPopup
      isVisible
      title=""
      onClose={onClose}
      scrollable
      fitContent
      containerClassName="careplus-cancel-modal-popup"
      contentClassName="careplus-cancel-modal-popup-content"
    >
      {screen === 'alternatives' && (
        <div className="careplus-cancel-modal">
          <p className="careplus-cancel-modal__title">
            <span className="careplus-cancel-modal__title-accent" style={{ backgroundImage: 'linear-gradient(144.63deg, #F59402 29.185%, #FFEDA6 158.31%)' }}>
              {sorryAccent}
            </span>
            {sorryRest ? ` ${sorryRest}` : ''}
          </p>
          <p className="careplus-cancel-modal__subtitle">{t('CarePlus/cancelModal/subtitle')}</p>

          <div className="careplus-cancel-modal__alt-group">
            <button type="button" className="careplus-cancel-modal__option" disabled={isPaused} onClick={() => handleAlternativeAction('pause')}>
              <span className="careplus-cancel-modal__option-icon">
                <img src={altPauseIcon} alt="" width={17} height={18} />
              </span>
              <span className="careplus-cancel-modal__option-text">
                <span className="careplus-cancel-modal__option-title">{t('CarePlus/cancelModal/pauseTitle')}</span>
                <span className="careplus-cancel-modal__option-desc">{t('CarePlus/cancelModal/pauseDesc')}</span>
              </span>
            </button>
            <div className="careplus-cancel-modal__alt-subgroup">
              <button type="button" className="careplus-cancel-modal__option" disabled onClick={() => handleAlternativeAction('downgrade')}>
                <span className="careplus-cancel-modal__option-icon">
                  <img src={altDowngradeIcon} alt="" width={27} height={27} />
                </span>
                <span className="careplus-cancel-modal__option-text">
                  <span className="careplus-cancel-modal__option-title">{t('CarePlus/cancelModal/downgradeTitle')}</span>
                  <span className="careplus-cancel-modal__option-desc">{t('CarePlus/cancelModal/downgradeDesc')}</span>
                </span>
              </button>
              <button type="button" className="careplus-cancel-modal__option" disabled onClick={() => handleAlternativeAction('medOnly')}>
                <span className="careplus-cancel-modal__option-icon">
                  <img src={altMedicationIcon} alt="" width={27} height={27} />
                </span>
                <span className="careplus-cancel-modal__option-text">
                  <span className="careplus-cancel-modal__option-title">
                    {medOnlyTitle}
                    {medOnlySuffix ? <span className="careplus-cancel-modal__option-title-sub"> ({medOnlySuffix}</span> : ''}
                  </span>
                  <span className="careplus-cancel-modal__option-desc">{t('CarePlus/cancelModal/medOnlyDesc')}</span>
                </span>
              </button>
            </div>
          </div>

          <div className="careplus-cancel-modal__alt-subgroup">
            <button
              type="button"
              className="careplus-cancel-modal__option careplus-cancel-modal__option--neutral"
              onClick={() => handleAlternativeAction('cancel')}
            >
              <span className="careplus-cancel-modal__option-icon">
                <img src={altCancelIcon} alt="" width={31} height={31} />
              </span>
              <span className="careplus-cancel-modal__option-text">
                <span className="careplus-cancel-modal__option-title">{t('CarePlus/cancelModal/cancelTitle')}</span>
                <span className="careplus-cancel-modal__option-desc">{t(isMonthlyPlan ? 'CarePlus/monthlyPlan/cancelDesc' : 'CarePlus/cancelModal/cancelDesc')}</span>
              </span>
            </button>
            <button
              type="button"
              className="careplus-cancel-modal__option careplus-cancel-modal__option--neutral"
              onClick={() => handleAlternativeAction('keep')}
            >
              <span className="careplus-cancel-modal__option-icon">
                <img src={keepIcon} alt="" width={28} height={28} />
              </span>
              <span className="careplus-cancel-modal__option-text">
                <span className="careplus-cancel-modal__option-title">{t('CarePlus/cancelModal/keepTitle')}</span>
                <span className="careplus-cancel-modal__option-desc">{t('CarePlus/cancelModal/keepDesc')}</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {screen === 'reasons' && (
        <div className="careplus-cancel-modal">
          <p className="careplus-cancel-modal__title">
            <span className="careplus-cancel-modal__title-accent" style={{ backgroundImage: 'linear-gradient(144.63deg, #F59402 29.185%, #FFEDA6 158.31%)' }}>
              {sorryAccent}
            </span>
            {sorryRest ? ` ${sorryRest}` : ''}
          </p>
          <p className="careplus-cancel-modal__subtitle">{t('CarePlus/cancelModal/subtitle')}</p>

          <div className="careplus-cancel-modal__reasons">
            {CANCEL_REASONS.map((reason) => (
              <label key={reason.id} className={`careplus-cancel-modal__reason ${selectedReason === reason.id ? 'careplus-cancel-modal__reason--selected' : ''}`}>
                <span className="careplus-cancel-modal__reason-label">{t(reason.key)}</span>
                <span className="careplus-cancel-modal__reason-radio">
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={reason.id}
                    checked={selectedReason === reason.id}
                    onChange={() => setSelectedReason(reason.id)}
                  />
                </span>
              </label>
            ))}
          </div>
          {selectedReason === 'other' && (
            <textarea
              className="careplus-cancel-modal__comment"
              placeholder={t('CarePlus/cancelModal/otherPlaceholder') || ''}
              value={otherComment}
              onChange={(e) => setOtherComment(e.target.value)}
            />
          )}
          {renderKeepCancelButtons(handleSubmitCancel, !selectedReason || loading)}
        </div>
      )}

      {screen === 'refund' && (
        <div className="careplus-cancel-modal">
          <p className="careplus-cancel-modal__title">
            <span className="careplus-cancel-modal__title-accent" style={{ backgroundImage: 'linear-gradient(144.63deg, #F59402 29.185%, #FFEDA6 158.31%)' }}>
              {cancelAccent}
            </span>
            {cancelRest ? ` ${cancelRest}` : ''}
          </p>
          <p className="careplus-cancel-modal__desc">
            {isMonthlyPlan ? t('CarePlus/monthlyPlan/cancelDesc') : (
              <>
                {cancelDescPrefix}
                <CarePlusWordmark fontSize={19.47} />
                {cancelDescSuffix}
              </>
            )}
          </p>

          <div className="careplus-cancel-modal__refund-box">
            <span className="careplus-cancel-modal__refund-heading">{t('CarePlus/cancelModal/refundTitle')}</span>
            <div className="careplus-cancel-modal__refund-lines">
              <div className="careplus-cancel-modal__refund-row">
                <span className="careplus-cancel-modal__refund-label">
                  {(t('CarePlus/cancelModal/remainingPeriod') || '')
                    .replace(':used', String(refund?.monthsConsumed ?? 0))
                    .replace(':total', String(refund?.periodMonths ?? 0))}
                </span>
                <span className="careplus-cancel-modal__refund-amount">{translator.getPrice(refund?.remainingAmount ?? 0, locale)}</span>
              </div>
              {(refund?.videoCalls?.unused ?? 0) > 0 && (
                <div className="careplus-cancel-modal__refund-row">
                  <span className="careplus-cancel-modal__refund-label">{t('CarePlus/cancelModal/videoConsultations')}</span>
                  <span className="careplus-cancel-modal__refund-amount">{translator.getPrice(refund?.videoCalls?.ratePerCall ?? 0, locale)} x{refund?.videoCalls?.unused ?? 0}</span>
                </div>
              )}
              <div className="careplus-cancel-modal__refund-row">
                <span className="careplus-cancel-modal__refund-label">{t('CarePlus/cancelModal/minusDiscount')}</span>
                <span className="careplus-cancel-modal__refund-amount">-{translator.getPrice(refund?.discount ?? 0, locale)}</span>
              </div>
              <div className="careplus-cancel-modal__refund-row careplus-cancel-modal__refund-row--total">
                <span className="careplus-cancel-modal__refund-label">{t('CarePlus/cancelModal/estimatedRefund')}</span>
                <span className="careplus-cancel-modal__refund-amount">{refundAmountText}</span>
              </div>
            </div>
          </div>

          {renderKeepCancelButtons(() => setScreen('reasons'), false)}
        </div>
      )}

      {screen === 'confirm' && (
        <div className="careplus-cancel-modal careplus-cancel-modal__confirm">
          <div className="careplus-cancel-modal__badge-wrap">
            <img src={successBadge} alt="" className="careplus-cancel-modal__badge" />
            <img src={successCancelOverlay} alt="" className="careplus-cancel-modal__badge-overlay" />
          </div>
          <p className="careplus-cancel-modal__confirm-title">
            {confirmTitleRest ? `${confirmTitleRest} ` : ''}
            <span className="careplus-cancel-modal__confirm-title-accent" style={{ backgroundImage: 'linear-gradient(200.84deg, #FFB909 0.8%, #E17200 94.83%)' }}>
              {confirmTitleAccent}
            </span>
          </p>
          <div className="careplus-cancel-modal__confirm-copy">
            <p className="careplus-cancel-modal__confirm-body">
              {isMonthlyPlan ? t('CarePlus/monthlyPlan/cancelConfirmBody') : (
                <>
                  {confirmBodyPrefix}
                  <CarePlusWordmark />
                  {' '}
                  {planTypeLabel}
                  {confirmBodySuffix}
                </>
              )}
            </p>
            <p className="careplus-cancel-modal__confirm-refund">
              {confirmRefundPrefix}
              <span className="careplus-cancel-modal__confirm-refund-amount">{refundAmountText} </span>
              {confirmRefundRest}
            </p>
            <p className="careplus-cancel-modal__confirm-resubscribe">{t('CarePlus/cancelModal/confirmResubscribe')}</p>
          </div>
          <button
            type="button"
            className="careplus-cancel-modal__btn careplus-cancel-modal__btn--done"
            onClick={onSuccess}
          >
            {t('CarePlus/cancelModal/confirmButton')}
          </button>
        </div>
      )}
    </AnimatedPopup>
  );
};

export default CarePlusCancelModal;
